import { Box, Button, InputAdornment, Modal, TextField, Typography } from "@mui/material";
import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../../axiosInstance";
import {
  BUTTON_STYLES,
  FORM_STYLES,
  MODAL_STYLES,
  TEXT_FIELD_STYLES,
} from "../../styles/componentStyles";

// item1..item6 = percent off when the cart has 1..6 books
export const COMBO_ITEMS = [1, 2, 3, 4, 5, 6].map((n) => ({
  key: `item${n}`,
  label: `${n} Book${n > 1 ? "s" : ""}`,
}));

// Same limits as the column: NUMERIC(5,2) between 0 and 100, empty allowed
const percent = Yup.number()
  .transform((value, original) => (original === "" ? undefined : value))
  .typeError("Enter a number")
  .min(0, "Must be 0 or more")
  .max(100, "Must be 100 or less")
  .test("decimals", "At most 2 decimal places", (v) => v == null || /^\d+(\.\d{1,2})?$/.test(String(v)));

const validationSchema = Yup.object(
  Object.fromEntries(COMBO_ITEMS.map((i) => [i.key, percent])),
);

function ComboDiscountModal({ open, onClose, comboEdit, onSuccess }) {
  const isEdit = Boolean(comboEdit?.id);

  const formik = useFormik({
    initialValues: Object.fromEntries(
      COMBO_ITEMS.map((i) => [i.key, comboEdit?.[i.key] ?? ""]),
    ),
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      const payload = Object.fromEntries(
        COMBO_ITEMS.map((i) => [i.key, values[i.key] === "" ? null : Number(values[i.key])]),
      );
      try {
        const response = isEdit
          ? await api.put(`/api/combo-discount-prices/${comboEdit.id}`, payload)
          : await api.post("/api/combo-discount-prices", payload);
        toast.success(`Combo discount ${isEdit ? "updated" : "added"} successfully!`, {
          position: "top-right",
          autoClose: 2000,
        });
        onSuccess(response.data.data);
        onClose();
      } catch (error) {
        toast.error(
          error.response?.data?.errorMessage || "Failed to submit. Please try again.",
          { autoClose: 3000 },
        );
        console.error("API Error:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <Box
        sx={{ ...MODAL_STYLES.standard, width: { xs: 340, md: 500 } }}
        component="form"
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <Typography variant="h5" component="h2" sx={{ mb: 1, fontWeight: 600 }}>
          {isEdit ? "Update Combo Discount" : "Add Combo Discount"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Discount percentage applied when the cart has that many books. Leave a
          field empty for no value.
        </Typography>
        <Box sx={FORM_STYLES.container}>
          {COMBO_ITEMS.map((i) => (
            <TextField
              key={i.key}
              label={`${i.label} (%)`}
              name={i.key}
              type="number"
              value={formik.values[i.key]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched[i.key] && Boolean(formik.errors[i.key])}
              helperText={formik.touched[i.key] && formik.errors[i.key]}
              inputProps={{ min: 0, max: 100, step: "0.01" }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              {...TEXT_FIELD_STYLES.standard}
            />
          ))}
          <Box
            sx={FORM_STYLES.actions}
            style={{ display: "flex", marginTop: "20px", justifyContent: "flex-end", gap: "10px" }}
          >
            <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
              Cancel
            </Button>
            <Button type="submit" disabled={formik.isSubmitting} {...BUTTON_STYLES.primary}>
              {formik.isSubmitting ? "Processing..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default ComboDiscountModal;
